/**
 * startSceneNow - start a scene through the SceneManager straight away, even
 * when a stop for the same scene is still waiting in Phaser's operation queue.
 *
 * MUS-576: an exercise scene ends itself with `this.scene.stop()`. That call
 * goes through the ScenePlugin, which only queues the stop; Phaser applies its
 * queue at the top of the next game step. `SceneManager.stop()` and
 * `SceneManager.start()` are not queued, so a caller that restarts the scene in
 * the gap between the two runs first, and one frame later the queued stop shuts
 * the fresh run down. Nothing starts it again: in a tutorial the next chapter's
 * exercise never loads.
 *
 * The queued stop was meant for the run that this call replaces, and this call
 * shuts that run down itself, so the stop is dropped rather than left to fire
 * on the new run. Everything else in the queue is left alone.
 */

import type Phaser from 'phaser';
import logger from 'Utils/Logger';

interface QueuedSceneOperation {
  op: string;
  keyA: string | Phaser.Scene;
}

/** Phaser keeps the queue on a field its typings do not declare. */
interface SceneOperationQueue {
  _queue: QueuedSceneOperation[];
}

const hasOperationQueue = <Manager extends object>(
  manager: Manager,
): manager is Manager & SceneOperationQueue => '_queue' in manager && Array.isArray(manager._queue);

/**
 * Drop every stop queued for `key`. Returns how many were dropped, or
 * undefined when this Phaser build does not expose the queue.
 *
 * Call it from outside the game step only: Phaser walks the queue by index
 * while it applies it.
 */
export function discardQueuedStops(
  manager: Phaser.Scenes.SceneManager,
  key: string,
): number | undefined {
  if (!hasOperationQueue(manager)) return undefined;
  const target = manager.getScene(key);
  if (!target) return 0;
  const queued = manager._queue;
  const kept = queued.filter(
    (entry) => !(entry.op === 'stop' && manager.getScene(entry.keyA) === target),
  );
  manager._queue = kept;
  return queued.length - kept.length;
}

/**
 * Shut down whatever run of `key` exists and start a new one with `data`, in
 * this call rather than on the next game step.
 */
export function startSceneNow(
  manager: Phaser.Scenes.SceneManager,
  key: string,
  data: object,
): void {
  if (discardQueuedStops(manager, key) === undefined) {
    logger.warn(
      `[startSceneNow] scene queue not readable; a stop already queued for ${key} can end the new run`,
    );
  }
  manager.stop(key);
  manager.start(key, data);
}
